"use client";

import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface TypographyVariantsOptions {
    heading1?: React.CSSProperties;
    heading2?: React.CSSProperties;
    heading3?: React.CSSProperties;
    heading4?: React.CSSProperties;
    subheadingMedium?: React.CSSProperties;
    subheading1?: React.CSSProperties;
    subheading1Semi?: React.CSSProperties;
    subheading2?: React.CSSProperties;
    subheading3?: React.CSSProperties;
    mainTextLarge?: React.CSSProperties;
    mainTextLargeSemi?: React.CSSProperties;
    mainTextLargeMedium?: React.CSSProperties;
    mainTextLargeBold?: React.CSSProperties;
    mainText?: React.CSSProperties;
    mainTextMedium?: React.CSSProperties;
    mainTextSemi?: React.CSSProperties;
    mainTextBold?: React.CSSProperties;
    secondaryText?: React.CSSProperties;
    secondaryTextMedium?: React.CSSProperties;
    secondaryTextSemi?: React.CSSProperties;
    tiny?: React.CSSProperties;
    tinyLight?: React.CSSProperties;
    tinyBold?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    heading1: true;
    heading2: true;
    heading3: true;
    heading4: true;
    subheadingMedium: true;
    subheading1: true;
    subheading1Semi: true;
    subheading2: true;
    subheading3: true;
    mainTextLarge: true;
    mainTextLargeSemi: true;
    mainTextLargeMedium: true;
    mainTextLargeBold: true;
    mainText: true;
    mainTextMedium: true;
    mainTextSemi: true;
    mainTextBold: true;
    secondaryText: true;
    secondaryTextMedium: true;
    secondaryTextSemi: true;
    tiny: true;
    tinyLight: true;
    tinyBold: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#46d64f",
      dark: "#009b45",
      light: "#fef0ea",
    },
    secondary: {
      main: "#111827",
    },
    error: {
      main: "#E03137",
    },
    success: {
      main: "#27A376",
    },
  },
  typography: {
    fontFamily: "Inter",
    htmlFontSize: 16,
    heading1: {
      fontWeight: 700,
      fontSize: "2rem", // 32px
      color: "var(--color-text-black)",
      lineHeight: "125%",
    },
    heading2: {
      fontWeight: 700,
      fontSize: "1.75rem", // 28px
      color: "var(--color-text-black)",
      lineHeight: "130%",
    },
    heading3: {
      fontWeight: 700,
      fontSize: "1.5rem", // 24px
      color: "var(--color-text-black)",
      lineHeight: "130%",
    },
    heading4: {
      fontWeight: 700,
      fontSize: "1.312rem", // 21px
      color: "var(--color-text-black)",
      lineHeight: "120%",
    },
    subheadingMedium: {
      fontWeight: 500,
      fontSize: "1.312rem", // 21px
      color: "var(--color-text-black)",
      lineHeight: "120%",
    },
    subheading1: {
      fontWeight: 700,
      fontSize: "1.25rem", // 20px
      color: "var(--color-text-black)",
      lineHeight: "1.625rem",
    },
    subheading1Semi: {
      fontWeight: 600,
      fontSize: "1.25rem", // 20px
      color: "var(--color-text-black)",
      lineHeight: "1.625rem",
    },
    subheading2: {
      fontWeight: 700,
      fontSize: "1.125rem", // 18px
      color: "var(--color-text-black)",
      lineHeight: "1.4625rem",
    },
    subheading3: {
      fontWeight: 700,
      fontSize: "0.875rem", // 14px
      color: "var(--color-text-black)",
      lineHeight: "1.1375rem",
    },
    mainTextLarge: {
      fontWeight: 400,
      fontSize: "1.125rem", // 18px
      color: "var(--color-text-black)",
      lineHeight: "1.6875rem",
    },
    mainTextLargeMedium: {
      fontWeight: 500,
      fontSize: "1.125rem", // 18px
      color: "var(--color-text-black)",
      lineHeight: "1.6875rem",
    },
    mainTextLargeSemi: {
      fontWeight: 600,
      fontSize: "1.125rem",
      color: "var(--color-text-black)",
      lineHeight: "1.6875rem",
    },
    mainTextLargeBold: {
      fontWeight: 800,
      fontSize: "1.125rem",
      color: "var(--color-text-black)",
      lineHeight: "1.6875rem",
    },
    mainText: {
      fontWeight: 400,
      fontSize: "1rem",
      color: "var(--color-text-black)",
      lineHeight: "1.5rem",
    },
    mainTextMedium: {
      fontWeight: 500,
      fontSize: "1rem",
      color: "var(--color-text-black)",
      lineHeight: "1.5rem",
    },
    mainTextSemi: {
      fontWeight: 600,
      fontSize: "1rem",
      color: "var(--color-text-black)",
      lineHeight: "1.5rem",
    },
    mainTextBold: {
      fontWeight: 700,
      fontSize: "1rem",
      color: "var(--color-text-black)",
      lineHeight: "1.5rem",
    },
    secondaryText: {
      fontWeight: 400,
      fontSize: "0.875rem",
      color: "var(--color-text-black)",
      lineHeight: "1.3125rem",
    },
    secondaryTextMedium: {
      fontWeight: 500,
      fontSize: "0.875rem",
      color: "var(--color-text-black)",
      lineHeight: "1.3125rem",
    },
    secondaryTextSemi: {
      fontWeight: 600,
      fontSize: "0.875rem",
      color: "var(--color-text-black)",
      lineHeight: "1.3125rem",
    },
    tiny: {
      fontWeight: 600,
      fontSize: "0.75rem",
      color: "var(--color-text-black)",
      lineHeight: "1.125rem",
    },
    tinyLight: {
      fontWeight: 400,
      fontSize: "0.75rem",
      color: "var(--color-text-black)",
      lineHeight: "1.125rem",
    },
    tinyBold: {
      fontWeight: 700,
      fontSize: "0.75rem",
      color: "var(--color-text-black)",
      lineHeight: "1.125rem",
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        component: "span",
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        variant: "contained",
        size: "medium",
        color: "primary",
      },
      styleOverrides: {
        sizeLarge: {
          height: "56px",
          fontSize: 16,
          lineHeight: "150%",
          borderRadius: "10px",
        },
        sizeMedium: {
          height: "40px",
          fontSize: 14,
          lineHeight: "160%",
        },
        sizeSmall: {
          height: "19px",
          fontSize: 9.14,
          lineHeight: "110%",
        },
        contained: {
          borderRadius: "10px",
          fontWeight: 600,
          color: "var(--color-white)",
          textTransform: "initial",
          background: "linear-gradient(90deg, #2bc3e3 0%, #46d64f 62%)",

          "&:disabled": {
            backgroundColor: "#F1F2F4",
            color: "#A0AEC0",
          },
        },

        outlined: {
          borderRadius: "8.41px",
          fontWeight: 400,
          color: "333333",
          textTransform: "initial",
          border: "2px solid var(--color-text-black)",
          "&:disabled": {
            border: "2px solid #F1F1F1",
            color: "var(--color-w-dark)",
          },
        },

        text: {
          color: "var(--color-black)",

          textTransform: "initial",
          "&:disabled": {
            color: "var(--color-w-dark)",
          },
        },
        startIcon: {
          marginRight: "4px",
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "standard",
        color: "primary",
      },
      styleOverrides: {
        root: {
          "& .MuiInputAdornment-root": {
            margin: 0, // Remove default margin for adornments
          },
          "& .MuiInputAdornment-positionStart": {
            marginLeft: "8px", // Adjust left margin of start icon
          },
          "& .MuiInputAdornment-positionEnd": {
            marginRight: "8px", // Adjust right margin of end icon
          },
          "& input": {
            paddingLeft: "8px", // Ensure there's enough space for start icon without overlapping placeholder
            paddingRight: "8px", // Ensure space for end icon
          },
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px var(--color-white) inset",
            WebkitTextFillColor: "var(--color-text-black)",
          },
          "& input:-webkit-autofill:hover": {
            WebkitBoxShadow: "0 0 0 1000px var(--color-white) inset",
          },
          "& input:-webkit-autofill:focus": {
            WebkitBoxShadow: "0 0 0 1000px var(--color-white) inset",
          },
          "& input:-webkit-autofill:active": {
            WebkitBoxShadow: "0 0 0 1000px var(--color-white) inset",
          },
          "& .MuiInput-root": {
            backgroundColor: "var(--color-white)",
            fontSize: "16px",
            fontWeight: 500,
            color: "var(--color-text-black)",
            border: "1px solid #E9EAEC",
            borderRadius: "10px",

            "&::placeholder": {
              fontSize: "16px",
              fontWeight: 500,
              color: "#A0AEC0",
            },

            "&:before": {
              borderBottom: "1px solid #E9EAEC",
            },
            "&:hover:not(.Mui-disabled):before": {
              // borderBottom: "1px solid var(--color-black)",
            },
            "&.Mui-focused:after": {
              // borderBottom: "2px solid var(--color-black)",
            },
          },
          "& .MuiInput-root.Mui-error": {
            borderColor: "var(--color-text-error)",
            marginBottom: "4px",
          },
          "& .MuiInput-root.Mui-focused": {
            // borderColor: "var(--color-orange)",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "standard",
        color: "primary",
      },
      styleOverrides: {
        root: {
          backgroundColor: "var(--color-white)",
          fontSize: "16px",
          fontWeight: 500,
          color: "var(--color-text-black)",
          border: "1px solid transparent",
          "& .MuiInput-root": {
            backgroundColor: "var(--color-white)",
            fontSize: "16px",
            fontWeight: 500,
            color: "var(--color-text-black)",
            borderRadius: "10px",
            border: "1px solid transparent",

            "&::placeholder": {
              fontSize: "16px",
              fontWeight: 500,
              color: "#A0AEC0",
            },

            "&.Mui-error": {
              border: "1px solid red !important",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        standard: {
          fontSize: "14px",
          fontWeight: "medium",
          color: "var(--color-text-black)",
          left: "0",
          top: "-8px",
          transform: "unset",
          lineHeight: "160%",
          position: "relative",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          maxWidth: "100%",
          // position: "absolute",
          // top: "100%",
          lineHeight: "1rem",
          marginTop: 0,
          paddingLeft: "8px",
          "&.Mui-disabled": {
            color: "#F1F2F4",
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:focus": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        underline: {
          ".MuiInputAdornment-standard": {
            color: "var(--color-text-black)",
          },

          "&.MuiInput-underline": {
            marginTop: 0,
          },

          "&::before": {
            display: "none",
          },

          "&::after": {
            display: "none",
          },
          "&.Mui-error": {
            border: "1px solid red !important",
          },
        },

        input: {
          borderRadius: "10px",
          fontSize: "16px",
          lineHeight: "160%",
          color: "var(--color-text-black)",
          fontWeight: 400,
          padding: "0px 0px",
          height: "46px",

          "&.Mui-error": {
            border: "1px solid red !important",
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#CBD5E0",
          "&.Mui-checked": {
            // color: "var(--color-orange)",
          },
        },
      },
    },
  },
});

export default theme;
